import ActionButton from 'components/ActionButton';
import BackButton from 'components/BackButton';
import CopyToClipBoard from 'components/CopyToClipBoard';
import Currency from 'components/Currency/Currency';
import InfoModal from 'components/InfoModal';
import Warning from 'components/message/Warning/index';
import UploadInvoiceModal from 'components/UploadInvoiceModal';
import { toLower } from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  applyPOItemQuantityChanges,
  cancelPurchaseOrder,
  deleteInvoiceForPurchaseOrder,
  deletePurchaseOrder,
  fetchPurchaseOrderDetail,
  fulfillPurchaseOrder,
  removePOItemFromPurchaseOrder,
  reopenPurchaseOrder,
  revertPOItemQuantityChanges,
  sendPurchaseOrderToSupplier,
} from 'services';
import { DATE_FORMATS, PO_STATUS, baseURL, formatDate } from 'utils';
import { showError, showSuccess } from 'utils/notifications';
import { Loading } from '../../../components/loading/Loading';
import AddInventoryItems from './addInventoryItems';
import styles from './index.module.scss';
import OrderItems from './OrderItems';

const ACTION_LOADING_TYPES = {
  reopenPO: 'reopenPO',
  fulfillPO: 'fulfillPO',
  cancelPO: 'cancelPO',
  deletePO: 'deletePO',
};

const PurchaseOrderDetails = props => {
  const [poDetails, setPODetails] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<string>('');
  const [invoiceActionLoading, setInvoiceActionLoading] = useState<boolean>(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState<boolean>(false);
  const { match: { params: { poId }}} = props;
  const { purchaseOrder, purchaseOrderItems } = poDetails;
  const uploadInvoiceUrl = `${baseURL}/po-public-detail/${poId}`;  // change route 
  const isInvoicePresent = !!purchaseOrder?.invoice;
  const isPOStatusDraft = purchaseOrder?.status === PO_STATUS.DRAFT || purchaseOrder?.status === PO_STATUS.RE_OPENED || purchaseOrder?.status === PO_STATUS.REJECTED;

  const fetchPODetails = async () => {
    try {
      const response = await fetchPurchaseOrderDetail(poId);
      setPODetails(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPODetails();
  }, []);

  const cancelPO = async () => {
    setActionLoading(ACTION_LOADING_TYPES.cancelPO);
    try {
      await cancelPurchaseOrder(poId);
      await fetchPODetails();
      showSuccess('Purchase Order cancelled successfully');
    } catch (e) {
      showError(e);
    } finally {
      setActionLoading('');
    }
  };

  const deletePO = async () => {
    setActionLoading(ACTION_LOADING_TYPES.deletePO);
    try {
      await deletePurchaseOrder(poId);
      props.history.replace('/');
    } catch (e) {
      showError(e);
    } finally {
      setActionLoading('');
    }
  };

  const handlePOPrimaryAction = async () => {
    setActionLoading(ACTION_LOADING_TYPES.fulfillPO);
    if (isPOStatusDraft) {
      try {
        await sendPurchaseOrderToSupplier(purchaseOrder.objectId);
        fetchPODetails();
        showSuccess('Purchase Order sent to supplier.');
      } catch (e) {
        showError(e);
      }
    } else {
      try {
        await fulfillPurchaseOrder(purchaseOrder.objectId);
        fetchPODetails();
        showSuccess('Purchase Order fulfilled.');
      } catch (e) {
        showError(e);
      }
    }
    setActionLoading('');
  };

  const deleteInvoice = async () => {
    setInvoiceActionLoading(true);
    try {
      await deleteInvoiceForPurchaseOrder(poId);
      await fetchPODetails();
      showSuccess('Invoice deleted successfully.');
    } catch (e) {
      showError(e);
    } finally {
      setInvoiceActionLoading(false);
    }
  };

  const reopenPO = async () => {
    setActionLoading(ACTION_LOADING_TYPES.reopenPO);
    try {
      await reopenPurchaseOrder(poId);
      await fetchPODetails();
      showSuccess('PO reopened successfully');
    } catch (e) {
      showError(e);
    } finally {
      setActionLoading('');
    }
  };

  const revertPOItemQuantityChangesFn = async () => {
    try {
      await revertPOItemQuantityChanges(poId);
      await fetchPODetails();
      showSuccess('Revert PO item quantity changes successful');
    } catch (e) {
      showError(e);
    }
  };

  const applyPOItemQuantityChangesFn = async () => {
    try {
      await applyPOItemQuantityChanges(poId);
      await fetchPODetails();
      showSuccess('Apply PO item quantity changes successful');
    } catch (e) {
      showError(e);
    }
  };

  const onDeletePOItem = async(poItem) => {
    try {
      await removePOItemFromPurchaseOrder(poItem.objectId);
      await fetchPODetails();
      showSuccess('Item deleted successfully');
    } catch (e) {
      showError(e);
    }
  };

  const invoiceUploadSuccess = async () => {
    await fetchPODetails();
    showSuccess('Invoice uploaded succesfully.');
    setInvoiceModalOpen(false);
  };

  const updatePODetails = (items: Record<string, any>) => {
    setPODetails({ ...poDetails, purchaseOrderItems: items });
  };

  const [infoModalDetails, setInfoModalDetails] = useState({}) as any;

  const getPOActionSection = () => (
    <div className={styles['po-action-section']}>
      {isPOStatusDraft && (
        <ActionButton
          text={'Delete PO'}
          active={actionLoading !== ACTION_LOADING_TYPES.deletePO}
          isLoading={actionLoading === ACTION_LOADING_TYPES.deletePO}
          onClick={deletePO}
          customClass={styles['cancelled']}
        />
      )}
      {(purchaseOrder.status === PO_STATUS.SENT || purchaseOrder.status === PO_STATUS.ACCEPTED) && (
        <ActionButton
          text={'Cancel PO'}
          active={actionLoading !== ACTION_LOADING_TYPES.cancelPO}
          isLoading={actionLoading === ACTION_LOADING_TYPES.cancelPO}
          onClick={cancelPO}
          customClass={styles['cancelled']}
        />
      )}
      {(purchaseOrder.status !== PO_STATUS.FULFILLED && purchaseOrder.status !== PO_STATUS.CANCELLED && purchaseOrder.status !== PO_STATUS.REJECTED) && (
        <ActionButton
          text={isPOStatusDraft ? 'Send PO' : 'Fulfill PO'}
          active={actionLoading !== ACTION_LOADING_TYPES.fulfillPO}
          isLoading={actionLoading === ACTION_LOADING_TYPES.fulfillPO}
          onClick={handlePOPrimaryAction}
        />
      )}
      {(purchaseOrder.status === PO_STATUS.FULFILLED || purchaseOrder.status === PO_STATUS.REJECTED) && (
        <ActionButton
          text={'Reopen PO'}
          active={actionLoading !== ACTION_LOADING_TYPES.reopenPO}
          isLoading={actionLoading === ACTION_LOADING_TYPES.reopenPO}
          onClick={reopenPO}
        />
      )}
      {(purchaseOrder.status === PO_STATUS.FULFILLED || purchaseOrder.status === PO_STATUS.RE_OPENED) && (
        <>
          <ActionButton
            text={'Apply items quantity changes'}
            active={true}
            isLoading={false}
            onClick={()=>{
              setInfoModalDetails({
                title: 'Apply Quantities',
                message: 'Are you sure you want to apply the item quantities?',
                primaryButtonText: 'Apply',
                primaryButtonOnClick: applyPOItemQuantityChangesFn,
                secondaryButtonText: 'Cancel',
                visible: true,
              });
            }}
          />
          <ActionButton
            text={'Revert items quantity changes'}
            active={true}
            isLoading={false}
            onClick={()=>{
              setInfoModalDetails({
                title: 'Revert Quantities',
                message: 'Are you sure you want to revert the item quantities?',
                primaryButtonText: 'Revert',
                primaryButtonOnClick: revertPOItemQuantityChangesFn,
                secondaryButtonText: 'Cancel',
                visible: true,
              });
            }}
          />
        </>
      )}
    </div>
  );

  const getInvoiceDetailsSection = () => (
    <div className={styles['invoice-section']}>
      <div className={styles['invoice-header']}>
        <div className={styles['subheading']}>Invoice</div>
        <ActionButton
          text={`${isInvoicePresent ? 'Delete' : 'Upload'} Invoice`}
          active={!invoiceActionLoading}
          isLoading={invoiceActionLoading}
          onClick={isInvoicePresent ? deleteInvoice : () => setInvoiceModalOpen(true)}
          customClass={isInvoicePresent ? styles['delete'] : ''}
        />
      </div>
      <div>
        {isInvoicePresent ? (
          <div className={'d-flex'}>
            {/* <div className={styles['invoice-id']}>
              <span className={`${styles['invoice-label']} mr-2`}>ID</span>
              <span className={styles['id-detail']}>{purchaseOrder.invoiceId}</span>
            </div> */}
            <div className={`d-flex ${styles['invoice-url']}`}>
              <i className={'fa fa-file mr-2'} />
              <a href={purchaseOrder.invoice.url} target={'blank'}>{purchaseOrder.invoice.url}</a>
            </div>
            <CopyToClipBoard
              copyText={purchaseOrder.invoice.url}
            />
          </div>
        ) : (
          <>
            <div className={styles['upload-invoice-url-message']}>
              <span>Invoice Upload Link</span>
              <a
                id={'upload-url'}
                target={'blank'}
                className={styles['upload-invoice-url']}
                href={uploadInvoiceUrl}
              >
                {uploadInvoiceUrl}
              </a>
              <CopyToClipBoard
                copyText={uploadInvoiceUrl}
              />
            </div>
            <Warning message={'Please upload an Invoice for the fulfilled Purchase Order'} />
          </>
        )}
      </div>
    </div>
  );

  const getSupplierDetailsSection = () => (
    <div className={styles['supplier-details-container']}>
      <div className={styles['details-section']}>
        <div className={styles['info-group']}>
          <div className={styles['label']}>Supplier Details</div>
          <div className={styles['detail']}>{purchaseOrder.supplier.name}</div>
          <div className={styles['address']}>{purchaseOrder.supplier.address || 'N/A'}</div>
        </div>
        <div className={styles['info-group']}>
          <div className={styles['label']}>Supplier ID</div>
          <a
            href={`/suppliers/${purchaseOrder.supplier.objectId}`}
            className={styles['detail']}
          >
            {purchaseOrder.supplier.objectId}
          </a>
        </div>
        <div className={styles['info-group']}>
          <div className={styles['label']}>Email Address</div>
          <div className={styles['email']}>{purchaseOrder.supplier.email || 'N/A'}</div>
        </div>
        <div className={styles['info-group']}>
          <div className={styles['label']}>Phone Number</div>
          <div className={styles['phone']}>{purchaseOrder.supplier.phone || 'N/A'}</div>
        </div>
      </div>
    </div>
  );

  const getBasicDetailsSection = () => (
    <div className={styles['basic-po-details-container']}>
      <div className={styles['details-section']}>
        <div className={styles['info-group']}>
          <div className={styles['label']}>Purchase Order ID</div>
          <div className={styles['detail']}>{purchaseOrder.objectId}</div>
        </div>
        <div className={styles['info-group']}>
          <div className={styles['label']}>Purchase Order Title</div>
          <div className={styles['detail']}>{purchaseOrder.title}</div>
        </div>
        <div className={styles['info-group']}>
          <div className={styles['label']}>Total</div>
          <div className={styles['detail']}><Currency amount={purchaseOrder.subtotal} default='-' /></div>
        </div>
        <div className={styles['date']}>{formatDate(purchaseOrder.createdAt, DATE_FORMATS.MM_DD_YYYY___hh_mm_A)}</div>
        <div
          className={`${styles['status']} ${styles[`${toLower(purchaseOrder.status)}`]}`}
        >
          {toLower(purchaseOrder?.status)}
        </div>
      </div>
      <div className={styles['details-section']}>
        <div className={styles['info-group']}>
          <div className={styles['label']}>Expected Delivery Date</div>
          <div className={styles['delivery-date']}>{purchaseOrder?.deliveryDate ? formatDate(purchaseOrder.deliveryDate, DATE_FORMATS.MM_DD_YYYY___hh_mm_A) : '-'}</div>
        </div>
        <div className={styles['info-group']}>
          <div className={styles['label']}>Supplier&apos;s Comment</div>
          <div className={styles['detail']}>{purchaseOrder?.comment ?? '-'}</div>
        </div>
      </div>
    </div>
  );

  const getHeader = () => (
    <div className={styles['po-details-header']}>
      <div className={styles['header-text']}>Purchase Order Details</div>
      <div className={styles['header-buttons']}>
        <BackButton />
      </div>
    </div>
  );

  if (loading) return <Loading />;

  return (
    <>
      {invoiceModalOpen && (
        <UploadInvoiceModal
          poId={poId}
          onSuccess={invoiceUploadSuccess}
          toggleModal={() => setInvoiceModalOpen(!invoiceModalOpen)}
        />
      )}
      <div className={`${styles['po-details-main-container']}`}>
        {getHeader()}
        {getBasicDetailsSection()}
        {getSupplierDetailsSection()}
        {getInvoiceDetailsSection()}
        {purchaseOrder.status === PO_STATUS.DRAFT && <AddInventoryItems 
          selectedSupplierId={purchaseOrder?.supplier?.objectId} 
          onItemsAdd={fetchPODetails} 
          poId={poId} 
        />}
        <OrderItems
          {...props}
          items={purchaseOrderItems}
          setValue={updatePODetails}
          poStatus={purchaseOrder.status}
          fetchPODetails={fetchPODetails}
          onDeleteItem={purchaseOrder.status === PO_STATUS.DRAFT && onDeletePOItem}
        />
        {getPOActionSection()}
      </div>
      {infoModalDetails?.visible && 
        <InfoModal
          cancellable={true}
          title={infoModalDetails?.title}
          message={infoModalDetails?.message}
          toggleModal={() => setInfoModalDetails({})}
          primaryButtonText={infoModalDetails?.primaryButtonText}
          primaryButtonOnClick={infoModalDetails?.primaryButtonOnClick}
          secondaryButtonText={infoModalDetails?.secondaryButtonText}
          secondaryButtonOnClick={() => {
            setInfoModalDetails({});
          }}
          primaryButtonClass={styles['change-quantity-button']}
        />
      }
    </>
  );
};

export { PurchaseOrderDetails };
